/**
 * Applying a server cart to the local Shopify cart.
 *
 * The two things that make this harder than a diff-and-write:
 *
 *   1. `/cart/add.js` INCREMENTS an existing identical line rather than setting
 *      it, and it rejects the whole `items[]` if any single line fails. So adds
 *      go one at a time, and every add is verified against a readback.
 *   2. Two Shopify line items can share one canonical key (a legacy line with no
 *      `_warehouse_code` next to a synced one). Those must be repaired first or
 *      the extra line is invisible to every diff and rides through to checkout.
 */

import { fetchCart, groupCartLines } from "../cartService.js";
import type { Cart, CartLineGroup } from "../../types/cart.js";
import { fetchWithRetry } from "../../utils/fetchWithRetry.js";
import type { CanonicalCart } from "./canonical.js";
import { canonicalFromGroups, needsLineRepair, parseKey } from "./canonical.js";

/** Cart attributes written at checkout. Blanked after any full replay. */
const CHECKOUT_ATTRIBUTE_KEYS = [
  "DeliveryMethod",
  "ShippingChargeCount",
  "EstimatedShipping",
  "estimatedShippingCharge",
  "estimatedAvailableDate",
  "DeliveryMethodPrice",
  "DeliveryZoneFee",
  "DeliveryRate",
  "DeliveryRateLabel",
  "DeliveryZone",
  "DeliveryWindow",
  "DeliveryQuoteSource",
] as const;

export interface ApplyOptions {
  /** Canonical keys the user is editing right now; never touched by an apply. */
  readonly heldKeys?: ReadonlySet<string>;
  /** Canonical keys known to fail locally; skipped so they cannot wedge a cycle. */
  readonly skipKeys?: ReadonlySet<string>;
}

export interface ApplyOutcome {
  /** Cart as it actually is afterwards — the only valid source for the hash. */
  readonly cart: Cart;
  readonly canonical: CanonicalCart;
  /** Keys deliberately left alone because the user is editing them. */
  readonly heldKeys: readonly string[];
  /**
   * Keys this apply actually changed. The product page re-baselines exactly
   * these; re-baselining everything would mark the user's unsaved edits clean.
   */
  readonly appliedKeys: readonly string[];
  /** Keys that could not be replayed (deleted variant, exhausted stock). */
  readonly failedKeys: readonly string[];
  /** True if duplicate Shopify lines were collapsed. */
  readonly repaired: boolean;
}

async function postCart(path: string, body: unknown, label: string): Promise<void> {
  await fetchWithRetry(
    path,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    label,
  );
}

/**
 * Collapse duplicate Shopify lines that share one canonical key.
 *
 * Keeps the first line and zeroes the rest, preserving the group's total so
 * nothing the customer chose disappears.
 */
async function repairDuplicateLines(
  damaged: readonly CartLineGroup[],
): Promise<void> {
  const updates: Record<string, number> = {};
  damaged.forEach((group) => {
    group.lineKeys.forEach((lineKey, index) => {
      updates[lineKey] = index === 0 ? group.totalQuantity : 0;
    });
  });
  await postCart("/cart/update.js", { updates }, "Cart sync: repair duplicate lines");
}

/**
 * Bring the local Shopify cart to `target`.
 *
 * Never throws for a single bad line: an unbuyable variant is recorded in
 * `failedKeys` so the caller can exclude it from future cycles rather than
 * letting it wedge every apply forever.
 */
export async function applyTarget(
  target: CanonicalCart,
  options: ApplyOptions = {},
): Promise<ApplyOutcome> {
  const held = options.heldKeys ?? new Set<string>();
  const skip = options.skipKeys ?? new Set<string>();

  let cart = await fetchCart();
  let groups = groupCartLines(cart);

  // 1. Repair before anything reads the cart as canonical.
  const damaged = needsLineRepair(groups);
  const repaired = damaged.length > 0;
  if (repaired) {
    await repairDuplicateLines(damaged);
    cart = await fetchCart();
    groups = groupCartLines(cart);
  }

  const current = canonicalFromGroups(groups);
  const heldKeys: string[] = [];
  const failedKeys: string[] = [];
  const appliedKeys: string[] = [];

  // 2. Deletes and quantity changes for lines that already exist — one batched
  //    update.js keyed by Shopify line key, which is exact and never merges.
  const updates: Record<string, number> = {};

  current.forEach((quantity, key) => {
    if (skip.has(key)) return;
    const desired = target.get(key);
    const group = groups.get(key);
    if (!group) return;

    // Nothing to do for this line — do NOT report it as held just because the
    // user happens to be editing it. A held key freezes lastAppliedVersion, so
    // counting no-op holds wedges the client into re-applying the same version
    // every poll, forever.
    if (desired === quantity) return;

    if (held.has(key)) {
      heldKeys.push(key);
      return;
    }

    if (desired === undefined) {
      updates[group.lineKeys[0]] = 0;
    } else {
      updates[group.lineKeys[0]] = desired;
    }
    appliedKeys.push(key);
  });

  if (Object.keys(updates).length > 0) {
    await postCart("/cart/update.js", { updates }, "Cart sync: apply updates");
  }

  // 3. Adds, one line per request. A batched add is all-or-nothing, so a single
  //    dead variant would block every good line behind it.
  const additions: Array<{ key: string; variantId: number; warehouseCode: string; quantity: number }> = [];
  target.forEach((quantity, key) => {
    if (current.has(key) || skip.has(key) || quantity <= 0) return;
    // A genuinely new line the user is editing IS a real hold.
    if (held.has(key)) {
      heldKeys.push(key);
      return;
    }
    const parsed = parseKey(key);
    if (parsed) {
      additions.push({ key, ...parsed, quantity });
      appliedKeys.push(key);
    }
  });

  for (const addition of additions) {
    try {
      await postCart(
        "/cart/add.js",
        {
          items: [
            {
              id: addition.variantId,
              quantity: addition.quantity,
              properties: { _warehouse_code: addition.warehouseCode },
            },
          ],
        },
        `Cart sync: add ${addition.key}`,
      );
    } catch {
      // 404 (variant gone) or 422 (insufficient stock). Record and continue —
      // the rest of the cart must still sync.
      failedKeys.push(addition.key);
      const at = appliedKeys.indexOf(addition.key);
      if (at >= 0) appliedKeys.splice(at, 1);
    }
  }

  // 4. Verify. add.js increments, so a line can legitimately end up ABOVE the
  //    target if a concurrent submit added the same variant. Downward deviations
  //    (stock clamps) are accepted as reality; upward ones are corrected, because
  //    adopting them would push an inflated quantity to every other device.
  cart = await fetchCart();
  groups = groupCartLines(cart);
  const corrections: Record<string, number> = {};
  groups.forEach((group, key) => {
    if (held.has(key) || skip.has(key)) return;
    const desired = target.get(key);
    if (desired !== undefined && group.totalQuantity > desired) {
      corrections[group.lineKeys[0]] = desired;
      group.lineKeys.slice(1).forEach((lineKey: string) => {
        corrections[lineKey] = 0;
      });
    }
  });

  if (Object.keys(corrections).length > 0) {
    await postCart(
      "/cart/update.js",
      { updates: corrections },
      "Cart sync: correct over-applied lines",
    );
    cart = await fetchCart();
    groups = groupCartLines(cart);
  }

  return {
    cart,
    canonical: canonicalFromGroups(groups),
    heldKeys,
    appliedKeys,
    failedKeys,
    repaired,
  };
}

/**
 * Blank the checkout quote attributes.
 *
 * `/cart/clear.js` does NOT remove attributes or the note, so a restored cart
 * would otherwise carry the previous quote into checkout. Four of these keys do
 * not start with "Delivery" and two begin lowercase, so the list is hardcoded
 * rather than pattern-matched — a prefix filter silently leaves the two keys the
 * EASI integration reads.
 */
export async function blankCheckoutAttributes(): Promise<void> {
  const attributes: Record<string, string> = {};
  CHECKOUT_ATTRIBUTE_KEYS.forEach((key) => {
    attributes[key] = "";
  });
  await postCart(
    "/cart/update.js",
    { attributes },
    "Cart sync: blank checkout attributes",
  );
}

export { CHECKOUT_ATTRIBUTE_KEYS };
